import { useTranslation } from 'react-i18next';

import { RELATION_REFRESH_BULK_MICROBATCH_SIZE_MAX } from '@features/admin-sync-worker-settings';
import { AdminSyncWorkerSettingsCard } from '@widgets/admin-sync-worker-settings';

const mockSettings = {
  workerRuntimeEnabled: true,
  apiEnqueueEnabled: true,
  syncPipelineEnabled: true,
  deleteExecutionEnabled: false,
  deleteBacklogCron: '* * * * *',
  deleteBacklogLeaseMs: 60000,
  deleteBacklogMaxAttempts: 10,
  deleteBacklogBaseBackoffSeconds: 5,
  deleteBacklogMaxBackoffSeconds: 300,
  deleteVerificationTimeoutMs: 20000,
  deleteBacklogMaxRowsPerRun: 10,
  deleteBacklogClaimBatchSize: 5,
  deleteBacklogMaxDurationMs: 30000,
  deleteWebhookInboxMaxRecordsPerRun: 25,
  deleteWebhookInboxMaxIdsPerRecord: 100,
  createUpdateWebhookInboxMaxRecordsPerRun: 25,
  deleteWebhookInboxBulkAckMinRecords: 25,
  createUpdateWebhookInboxBulkAckMinRecords: 25,
  snapshotExecutionEnabled: true,
  snapshotSupervisorIntervalSeconds: 30,
  snapshotJobsFreshnessSlaSeconds: 30,
  snapshotDataTypesFreshnessSlaSeconds: 120,
  snapshotRelationQueueFreshnessSlaSeconds: 60,
  snapshotLeaseWindowSeconds: 45,
  snapshotHeartbeatIntervalSeconds: 5,
  globalDeltaCron: '0 */2 * * *',
  inventoryPostgresCountEnabled: true,
  inventoryPostgresCountCron: '0 3 * * *',
  inventoryBubbleCountEnabled: true,
  inventoryBubbleCountCron: '*/20 * * * *',
  analyticsGearEnabled: true,
  inventoryCountsStaleWarningThresholdMinutes: 60,
  syncMatchFreshnessHours: 24,
  columnParityRefreshEnabled: true,
  columnParityRefreshCron: '0 */6 * * *',
  columnParityRefreshMaxAttempts: 3,
  columnParityRefreshBaseBackoffSeconds: 60,
  columnParityRefreshMaxBackoffSeconds: 1800,
  dataValidationEnabled: false,
  dataValidationScheduleEnabled: false,
  dataValidationScheduleCron: '0 3 1 * *',
  dataValidationLookbackDays: 30,
  dataValidationSafetyLagDays: 2,
  dataValidationRepairWaitMinutes: 60,
  dataValidationMaxRowsPerTable: 10000,
  dataValidationDirtyRecheckAttempts: 3,
  dataValidationMaxTablesPerCampaign: 25,
  dataValidationMaxSampleLimit: 1000,
  dataValidationDefaultChunkSize: 100,
  dataValidationBubbleRequestConcurrency: 1,
  dataValidationBubbleRequestsPerMinute: 60,
  dataValidationReportRetentionDays: 30,
  dataValidationSnapshotRetentionDays: 7,
  dataValidationDirtySnapshotRetentionDays: 7,
  dataValidationRawReportRetentionDays: 30,
  dataValidationFinalReportRetentionDays: 90,
  dataValidationFreshnessDays: 35,
  dataValidationFullScanEnabled: false,
  dataValidationLeaseMs: 120000,
  dataValidationCleanupBatchSize: 25,
  dataValidationMaxReportBytes: 5242880,
  refreshBatchSize: 10,
  refreshBatchDelayMs: 100,
  relationRefreshCron: '*/15 * * * *',
  relationRefreshBatchLimit: 100,
  relationRefreshLeaseMs: 60000,
  relationRefreshLockTimeoutMs: 1500,
  relationRefreshStatementTimeoutMs: 10000,
  relationRefreshIdleInTxTimeoutMs: 5000,
  relationRefreshEnabled: true,
  relationRefreshBulkEnabled: false,
  relationRefreshBulkCron: '*/5 * * * *',
  relationRefreshBulkMicrobatchSize: RELATION_REFRESH_BULK_MICROBATCH_SIZE_MAX,
  relationRefreshBulkTimeBudgetMs: 30000,
  relationRefreshBulkLeaseMs: 120000,
  relationSnapshotExactRecountEnabled: true,
  relationSnapshotExactRecountCron: '0 */4 * * *',
  relationSnapshotExactRecountStatementTimeoutMs: 20000,
  relationSnapshotExactRecountMaxTablesPerRun: 25,
  exactIdSyncEnabled: true,
  exactIdTerminalRetentionDays: 7,
  exactIdNotFoundClassifierCron: '*/1 * * * *',
  exactIdNotFoundClassifierSafetyLagMinutes: 1,
  retentionCleanupIntervalMinutes: 30,
  syncHistoryCleanupBatchSize: 500,
  workerIncidentsCleanupBatchSize: 500,
  syncTableQueueCleanupBatchSize: 500,
  syncExactIdQueueCleanupBatchSize: 500,
  jobRunsCompletedCleanupBatchSize: 500,
  jobRunsFailedCancelledCleanupBatchSize: 500,
  syncCountAuditLeaseCleanupBatchSize: 500,
  relationDoneRowsCleanupBatchSize: 500,
  deletedRelationCleanupQueueBatchSize: 500,
  syncReconciliationRunsCleanupBatchSize: 500,
  syncEntityReviewBacklogCleanupBatchSize: 500,
  bubbleMutationSuccessCleanupBatchSize: 500,
  bubbleMutationNonSuccessRedactionBatchSize: 500,
  webhookInboxLedgerCleanupBatchSize: 1000,
  webhookDeltaFallbackEnabled: true,
  relationDeadRepairEnabled: false,
  relationDeadRepairBatchSize: 25,
  relationDeadRepairTimeBudgetMs: 30000,
  relationDeadRepairBubbleRateLimitPerMinute: 60,
  relationDeadRepairLeaseMs: 120000,
  relationDeadRepairMaxAttempts: 10,
  syncTableQueueTotalExecutionsLimit: 2,
  syncExactIdQueueTotalExecutionsLimit: 2,
  relationRefreshQueueTotalExecutionsLimit: 2,
  mediaOwnerSyncQueueTotalExecutionsLimit: 2,
  mediaImportQueueTotalExecutionsLimit: 2,
  relationDeadRepairRetentionDays: 7,
  relationDeadRepairRootCron: '* * * * *',
  relationDeadRepairContinuationDelayMs: 5000,
  deletedRelationCleanupEnabled: false,
  deletedRelationCleanupBatchSize: 25,
  deletedRelationCleanupRateLimitPerMinute: 60,
  deletedRelationCleanupLeaseMs: 120000,
  deletedRelationCleanupMaxAttempts: 10,
  deletedRelationCleanupRetentionDays: 7,
  syncReconciliationMaxIdsPerBubbleRecheckBatch: 50,
  syncReconciliationMaxConflictRecheckBatchSize: 50,
  syncReconciliationMaxPagesPerBucket: 50,
  syncReconciliationMaxBucketsPerRun: 1,
  syncReconciliationMaxActionsPerRun: 1000,
  syncReconciliationMaxDeleteActionsPerRun: 500,
  syncReconciliationMaxExactIdActionsPerRun: 500,
  syncReconciliationMaxDurationMs: 300000,
  syncReconciliationSafetyLagMinutes: 10,
  syncReconciliationBucketMaxAttempts: 5,
  syncReconciliationActionRetentionDays: 14,
  syncReconciliationTierMaxRunsPerTick: 5,
  syncReconciliationTierDefaultLookbackHours: 24,
  syncSymmetricReconciliationEnabled: false,
  countMismatchDelayedRecountDelayMinutes: 5,
  mirrorDeltaPageSize: 100,
  mirrorDeltaBatchSize: 50,
  mirrorDeltaWorkBatchSize: 1,
  mirrorDeltaPollIntervalSeconds: 2,
  maxActiveSyncTables: 3,
  exactIdMaxActiveTables: 2,
  deltaMaxActiveTables: 1,
  standardMainChunkSize: 100,
  heavyRelationMainChunkSize: 25,
  standardPostprocessMicrobatchSize: 50,
  heavyRelationPostprocessMicrobatchSize: 25,
  postprocessLeaseMs: 120000,
  postprocessTimeBudgetMs: 60000,
  mediaImportSmallConcurrency: 2,
  mediaImportSchedulerEnabled: true,
  mediaOwnerSyncSchedulerEnabled: true,
  mediaOwnerMissingReconcileIntervalMinutes: 240,
  mediaOwnerSyncClaimBatchSize: 1,
  mediaOwnerSyncProcessConcurrency: 1,
  mediaOwnerSyncMaxActive: 1,
  mediaOwnerSyncTickTimeBudgetMs: 5_000,
  mediaOwnerSyncDbPressureBreakerEnabled: true,
  mediaOwnerSyncClaimDbPressureThresholdMs: 1_500,
  testModeEnabled: false,
  testModeBubbleKey: null,
  testModeRecordLimit: 25,
};

export function SyncWorkerSettingsShowcase() {
  const { t } = useTranslation();

  return (
    <AdminSyncWorkerSettingsCard
      environmentLabel={t('uiLibrary.patterns.syncWorker.environment')}
      settings={mockSettings}
      effectiveSettings={mockSettings}
      settingsMeta={{
        updatedAt: '2026-05-03T15:25:30.492Z',
        updatedBy: 'system',
        clampedFields: {},
        snapshotExecution: {
          requestedEnabled: true,
          workerRuntimeEnabled: true,
          effectiveEnabled: true,
          reason: null,
        },
      }}
      isLoading={false}
      isSaving={false}
      isReadOnly
      onSubmit={async () => {}}
      serverError={null}
    />
  );
}
